/**
 * Cliente HTTP del backend. Maneja el token de sesión, errores estructurados
 * del API ({ error, mensaje, detalles }) y descarga de PDFs.
 */

export class ErrorApi extends Error {
  constructor(
    public estado: number,
    public codigo: string,
    mensaje: string,
    public detalles?: unknown,
  ) {
    super(mensaje);
  }
}

let token: string | null = localStorage.getItem('ausentia.token');

export function fijarToken(nuevo: string | null) {
  token = nuevo;
  if (nuevo) localStorage.setItem('ausentia.token', nuevo);
  else localStorage.removeItem('ausentia.token');
}

export function hayToken(): boolean {
  return !!token;
}

async function parsear(res: Response): Promise<unknown> {
  const texto = await res.text();
  if (!texto) return null;
  try {
    return JSON.parse(texto);
  } catch {
    return texto;
  }
}

export async function pedir<T = unknown>(
  metodo: string,
  url: string,
  cuerpo?: unknown,
  opciones: { conToken?: boolean } = { conToken: true },
): Promise<T> {
  const headers: Record<string, string> = {};
  if (cuerpo !== undefined) headers['Content-Type'] = 'application/json';
  if (opciones.conToken !== false && token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url, {
    method: metodo,
    headers,
    body: cuerpo !== undefined ? JSON.stringify(cuerpo) : undefined,
  });

  const datos = await parsear(res);
  if (!res.ok) {
    const d = datos as { error?: string; mensaje?: string; detalles?: unknown } | null;
    if (res.status === 401) fijarToken(null);
    throw new ErrorApi(
      res.status,
      d?.error ?? 'ERROR',
      d?.mensaje ?? `Error ${res.status}`,
      d?.detalles,
    );
  }
  return datos as T;
}

export const api = {
  get: <T>(url: string) => pedir<T>('GET', url),
  post: <T>(url: string, cuerpo?: unknown) => pedir<T>('POST', url, cuerpo),
  put: <T>(url: string, cuerpo?: unknown) => pedir<T>('PUT', url, cuerpo),
  delete: <T>(url: string, cuerpo?: unknown) => pedir<T>('DELETE', url, cuerpo),
  publico: {
    get: <T>(url: string) => pedir<T>('GET', url, undefined, { conToken: false }),
    post: <T>(url: string, cuerpo?: unknown) => pedir<T>('POST', url, cuerpo, { conToken: false }),
  },
};

/** Abre un PDF protegido (con token) en una pestaña nueva. */
export async function abrirPdf(url: string): Promise<void> {
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    const d = (await parsear(res)) as { mensaje?: string } | null;
    throw new ErrorApi(res.status, 'PDF', d?.mensaje ?? 'No se pudo abrir el PDF');
  }
  const blob = await res.blob();
  const objeto = URL.createObjectURL(blob);
  window.open(objeto, '_blank');
  setTimeout(() => URL.revokeObjectURL(objeto), 60_000);
}
